import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MediaCard from '@/components/feature/MediaCard';
import FilterBar from '@/pages/category/components/FilterBar';
import { movies, tvShows } from '@/mocks/media';
import { filterMedia, movieGenres, tvGenres, type MediaFilter } from '@/utils/media';

const PAGE_SIZE = 8;

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const isMovie = type !== 'tv';

  const [filter, setFilter] = useState<MediaFilter>({
    genres: [],
    regions: [],
    years: [],
    status: 'all',
    sort: 'updated',
  });
  const [page, setPage] = useState(1);

  useEffect(() => {
    setFilter({ genres: [], regions: [], years: [], status: 'all', sort: 'updated' });
    setPage(1);
  }, [type]);

  const source = isMovie ? movies : tvShows;

  const results = useMemo(() => filterMedia(source, filter), [source, filter]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = results.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const reset = () => {
    setFilter({ genres: [], regions: [], years: [], status: 'all', sort: 'updated' });
    setPage(1);
  };

  const title = isMovie ? '电影资源' : '电视剧资源';
  const desc = isMovie
    ? '汇聚动作、科幻、爱情、悬疑等全类型电影资源，每日更新最新上线影片，总有一部合你心意。'
    : '精选热播剧集与经典好剧，支持按连载状态、地区与年份筛选，追剧不迷路。';

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6">
      <nav className="mb-4 flex items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{isMovie ? '电影分类' : '电视剧分类'}</span>
      </nav>

      <div className="mb-6 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{title}</h1>
          <p className="mt-2 max-w-2xl text-sm text-foreground-600">{desc}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2 rounded-md bg-secondary-50 px-4 py-3 text-sm text-secondary-800">
          <i className="ri-stack-line text-xl text-secondary-600"></i>
          <div>
            <p className="font-bold text-foreground-950">{results.length} 部</p>
            <p className="text-xs text-foreground-500">符合当前条件</p>
          </div>
        </div>
      </div>

      <FilterBar
        genreOptions={(isMovie ? movieGenres : tvGenres).filter((g) => g !== '全部')}
        showStatus={!isMovie}
        filter={filter}
        onChange={(next) => {
          setFilter(next);
          setPage(1);
        }}
        onReset={reset}
      />

      {pageItems.length === 0 ? (
        <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h2 className="mt-4 text-lg font-bold text-foreground-800">没有找到匹配的影视资源</h2>
          <p className="mt-1 text-sm text-foreground-500">换个筛选条件试试吧</p>
          <button
            onClick={reset}
            className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            重置筛选条件
          </button>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
            {pageItems.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>

          <div className="mt-8 flex items-center justify-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={current === 1}
              className="flex h-9 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 text-sm text-foreground-600 disabled:cursor-not-allowed disabled:opacity-40 hover:enabled:border-primary-300 hover:enabled:text-primary-600"
            >
              <i className="ri-arrow-left-s-line"></i> 上一页
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`h-9 w-9 rounded-md text-sm transition-colors ${
                  p === current
                    ? 'bg-primary-500 font-medium text-background-50'
                    : 'border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={current === totalPages}
              className="flex h-9 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 text-sm text-foreground-600 disabled:cursor-not-allowed disabled:opacity-40 hover:enabled:border-primary-300 hover:enabled:text-primary-600"
            >
              下一页 <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </>
      )}
    </div>
  );
}